import { z } from 'zod';

/**
 * Lagerstand eines Artikels (2026-10-15): woraus sich Bestand, Reserviert,
 * Bestellt und geplanter Bedarf zusammensetzen — für die aufklappbaren
 * Kennzahlen der Artikel-Übersicht und das Register „Lagerstand".
 * GET /v1/inventory/cockpit/articles/:articleId/stock-position
 */

export const stockPositionReservationSchema = z.object({
  id: z.string().uuid(),
  quantity: z.number(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  productionOrderId: z.string().uuid().nullable(),
  /** Wann die Ware gebraucht wird: Projekt-Liefertermin minus Vorlauf (wie Lagercockpit); null = unbekannt. */
  neededOn: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type StockPositionReservation = z.infer<typeof stockPositionReservationSchema>;

export const stockPositionIncomingSchema = z.object({
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  supplierName: z.string(),
  /** DRAFT = noch nicht an den Lieferanten geschickt, ORDERED = bestellt. */
  status: z.enum(['DRAFT', 'ORDERED']),
  expectedDate: z.string().datetime().nullable(),
  orderedQty: z.number(),
  receivedQty: z.number(),
  openQty: z.number(),
});
export type StockPositionIncoming = z.infer<typeof stockPositionIncomingSchema>;

export const stockPositionDemandSchema = z.object({
  forecastId: z.string().uuid(),
  forecastName: z.string(),
  description: z.string(),
  projectText: z.string().nullable(),
  quantity: z.number(),
  expectedDate: z.string().datetime().nullable(),
});
export type StockPositionDemand = z.infer<typeof stockPositionDemandSchema>;

export const stockPositionSchema = z.object({
  articleId: z.string().uuid(),
  stockQty: z.number(),
  reservedQty: z.number(),
  minStock: z.number().nullable(),
  maxStock: z.number().nullable(),
  leadTimeDays: z.number().nullable(),
  reservations: z.array(stockPositionReservationSchema),
  incoming: z.array(stockPositionIncomingSchema),
  demand: z.array(stockPositionDemandSchema),
});
export type StockPosition = z.infer<typeof stockPositionSchema>;

export type StockEventKind = 'INCOMING' | 'RESERVATION' | 'DEMAND';

export interface StockEvent {
  kind: StockEventKind;
  /** Tag des Ereignisses (ISO); ohne eigenen Termin = heute. */
  date: string;
  /** true, wenn das Ereignis keinen eigenen Termin hat und auf heute gelegt wurde. */
  isUndated: boolean;
  /** Zugang positiv, Abgang negativ. */
  quantity: number;
  /** Projizierter Bestand nach diesem Ereignis. */
  stockAfter: number;
  ref: string;
  label: string;
  /** Bestellung: Termin überschritten. */
  isOverdue: boolean;
}

export interface StockTimeline {
  events: StockEvent[];
  freeQty: number;
  incomingQty: number;
  demandQty: number;
  /** Bestand + Bestellt − Reserviert. */
  calculatedQty: number;
  /** Kalk. Bestand − geplanter Bedarf. */
  afterDemandQty: number;
  /** Erstes Ereignis, nach dem der Bestand unter 0 fällt (null = reicht). */
  shortageDate: string | null;
  /** Spätester Bestelltermin: Unterdeckung minus Wiederbeschaffungszeit (null ohne Unterdeckung/WBZ). */
  orderByDate: string | null;
  overdueIncoming: StockPositionIncoming[];
}

const DAY_MS = 86_400_000;
const dayOf = (iso: string): string => iso.slice(0, 10);
const RANK: Record<StockEventKind, number> = { INCOMING: 0, RESERVATION: 1, DEMAND: 2 };

/**
 * Zeitachse des Lagerstands (rein, testbar): Ab dem heutigen Bestand wird
 * jede offene Bestellung am Liefertermin zugebucht, jede Reservierung an ihrem
 * Bedarfstermin und jeder geplante Bedarf an seinem Termin abgezogen.
 * Was keinen Termin hat — oder schon überfällig ist — zählt ab heute: Eine
 * überfällige Bestellung gilt als „kommt jetzt", eine Reservierung ohne Termin
 * als sofort gebraucht. Am selben Tag erst Zugänge, dann Abgänge.
 */
export function buildStockTimeline(
  position: StockPosition,
  options: { now: Date; includeDemand?: boolean },
): StockTimeline {
  const today = options.now.toISOString().slice(0, 10);
  const includeDemand = options.includeDemand ?? true;
  const atLeastToday = (iso: string | null): { date: string; isUndated: boolean } =>
    iso && dayOf(iso) >= today ? { date: dayOf(iso), isUndated: false } : { date: today, isUndated: !iso };

  const raw: Omit<StockEvent, 'stockAfter'>[] = [];
  for (const order of position.incoming) {
    if (order.openQty <= 0) continue;
    const isOverdue = Boolean(order.expectedDate && dayOf(order.expectedDate) < today);
    raw.push({ kind: 'INCOMING', ...atLeastToday(order.expectedDate), quantity: order.openQty, ref: order.orderNumber, label: order.supplierName, isOverdue });
  }
  for (const reservation of position.reservations) {
    raw.push({
      kind: 'RESERVATION',
      ...atLeastToday(reservation.neededOn),
      quantity: -reservation.quantity,
      ref: reservation.projectCode ?? 'Fertigungsauftrag',
      label: reservation.projectName ?? (reservation.productionOrderId ? 'Fertigungsauftrag' : 'Ohne Zuordnung'),
      isOverdue: false,
    });
  }
  if (includeDemand) {
    for (const line of position.demand) {
      if (line.quantity <= 0) continue;
      raw.push({
        kind: 'DEMAND',
        ...atLeastToday(line.expectedDate),
        quantity: -line.quantity,
        ref: line.forecastName,
        label: line.projectText ? `${line.description} · ${line.projectText}` : line.description,
        isOverdue: false,
      });
    }
  }
  raw.sort((a, b) => a.date.localeCompare(b.date) || RANK[a.kind] - RANK[b.kind]);

  let running = position.stockQty;
  const events: StockEvent[] = raw.map((event) => {
    running += event.quantity;
    return { ...event, stockAfter: running };
  });

  const incomingQty = position.incoming.reduce((sum, order) => sum + Math.max(0, order.openQty), 0);
  const demandQty = position.demand.reduce((sum, line) => sum + Math.max(0, line.quantity), 0);
  const calculatedQty = position.stockQty + incomingQty - position.reservedQty;
  const shortage = events.find((event) => event.stockAfter < 0) ?? null;
  const orderByDate =
    shortage && position.leadTimeDays != null
      ? new Date(new Date(`${shortage.date}T00:00:00.000Z`).getTime() - position.leadTimeDays * DAY_MS).toISOString().slice(0, 10)
      : null;

  return {
    events,
    freeQty: position.stockQty - position.reservedQty,
    incomingQty,
    demandQty,
    calculatedQty,
    afterDemandQty: calculatedQty - demandQty,
    shortageDate: shortage?.date ?? null,
    orderByDate,
    overdueIncoming: position.incoming.filter(
      (order) => order.openQty > 0 && order.expectedDate !== null && dayOf(order.expectedDate) < today,
    ),
  };
}
